import { CARDS_PER_STOP, CARD_POOL, CITY_LEG_KM, CITY_MAX_ALTITUDE, FOV_TAN_HALF } from "./reelConstants";
import { cardImageName, drawPhotoCard, stopPhotos } from "./photoCards";
import { planLegs } from "./reelAltitudes";
import { showApprovedFrame } from "./approvedFrame";
import { geojsonSourceIds, hasImports, keyframesInRange, planWarmupKeyframes, sourcesLoaded, tileSourceIds, tilesLoaded, warmTiles } from "./tileWarmup";
import * as Poppins from "@remotion/google-fonts/Poppins";

import * as Rubik from "@remotion/google-fonts/Rubik";
import bearing from "@turf/bearing";
import bezierSpline from "@turf/bezier-spline";
import greatCircle from "@turf/great-circle";
import { lineString, point } from "@turf/helpers";
import length from "@turf/length";

import TextTag from "components/cards/TextTag";
import StipplLogo from "components/website/img/stippl-logo.svg";
import { first, last } from "lodash";
import mapboxgl, { Map } from "mapbox-gl";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { AbsoluteFill, Composition, OffthreadVideo, Sequence, Series, continueRender, delayRender, getRemotionEnvironment, staticFile, useCurrentFrame } from "remotion";
import { getGeocodes, getItemID } from "utils/getters";
import mapChip from "../../img/markers/map-chip.png";
import "./TripReel.scss";
// import Logo from "./img/goboony_logo.png";
// import UserBadge from "./img/goboony_userbadge.png";
import PoweredBy from "./img/poweredBy.svg";

import { getLightColor } from "components/pdfexport/elements/BrandingContext";
import { DEFAULT_B2B_THEME } from "constants/color";
import { MARKER_ICON_COLOR_AGREED } from "components/map/markerVisualTokens";
import { withRoadRoutes } from "./roadRoutes";
import html2canvas from "html2canvas";
import Bus from "./icons/bus.png";
import Car from "./icons/car.png";
import Ferry from "./icons/ferry.png";
import Plane from "./icons/plane.png";
import Train from "./icons/train.png";
import Walk from "./icons/walk.png";
import Bike from "./icons/bike.png";
import Taxi from "./icons/taxi.png";
import VenueCocktail from "./icons/venue-cocktail.png";
import VenueBeer from "./icons/venue-beer.png";
import VenueWine from "./icons/venue-wine.png";
import VenueTropical from "./icons/venue-tropical.png";
import VenueBeach from "./icons/venue-beach.png";
import VenueWaterfall from "./icons/venue-waterfall.png";
import VenueViewpoint from "./icons/venue-viewpoint.png";
import VenueMarket from "./icons/venue-market.png";
import CardShadow from "./icons/card-shadow.png";
import Card1 from "./icons/card-1.png";
import Card2 from "./icons/card-2.png";
import Card3 from "./icons/card-3.png";
import Card4 from "./icons/card-4.png";
import Card5 from "./icons/card-5.png";
import Card6 from "./icons/card-6.png";
import "./reeldestinationmarker/ReelDestinationMarker.scss";
import "./reeltitle/ReelTitle.scss";
import IntroSequence from "./sequences/IntroSequence";
import MarkerSequence from "./sequences/MarkerSequence";
import PathSequence from "./sequences/PathSequence";
import FinishedSequences from "./sequences/FinishedSequences";
import ReelOutro, { REEL_OUTRO_FRAMES } from "./reeloutro/ReelOutro";
import PhotoSequence from "./sequences/PhotoSequence";
import ClipIntro from "./sequences/ClipIntro";
import { introGeometry, introWarmupKeyframes } from "./sequences/introCamera";
import { CLASSIC_INTRO_FRAMES, introFrames, introOverhang, introStyleOf } from "./sequences/introPlan";
import { legHeadingAt } from "./sequences/legHeading";

export const FPS = 30;
/** The classic intro. How long the intro of a given reel is comes from `introFrames(styles)`. */
export const INTRO = CLASSIC_INTRO_FRAMES;
export { CARDS_PER_STOP, CARD_POOL, CITY_LEG_KM, CITY_MAX_ALTITUDE };
export const MARKER_DURATION = 30;

/**
 * The most relief a reel's map gets unless it asks for more. Mapbox's satellite style (Satellite
 * Streets v1.1, the reels' "satellite") ships its terrain at 7.7x: over Jordan's rift valley, the
 * Andes or the Alps the camera, which is placed by its altitude above sea level, flew straight
 * through the mountains. At 1.5x the relief still reads and stays under the camera.
 */
export const RELIEF_CAP = 1.5;
export const cappedTerrain = (terrain: any) =>
	terrain && typeof terrain.exaggeration === "number" && terrain.exaggeration > RELIEF_CAP ? { ...terrain, exaggeration: RELIEF_CAP } : null;
const MAX_MAP_DURATION = 80;
export const PHOTO_DURATION = 25;
/** The closing card. Its beats live in ReelOutro; this is the same number, once. */
export const OUTRO_DURATION = REEL_OUTRO_FRAMES;
const START_ALTITUDE = 250000;
// 60 rather than 50. On a local trip the camera sits low enough that the extra ten degrees is what
// makes elevation and buildings read as height rather than as texture. Mapbox allows up to 85.
const START_PITCH = 60;
/** tan(fov/2) for Mapbox's default 36.87 degree field of view. */

/**
 * Flatten turf's antimeridian-split MultiLineString into one continuous LineString.
 *
 * The shift has to apply to EVERY point of a following segment, not just its first. Correcting only
 * the first point left the other 46 on the far side of the date line, so the flattened line carried
 * a 359.8 degree jump (measured on Sydney to Oahu: index 54 at 180.00, index 55 at 180.00, index 56
 * back at -179.76).
 *
 * Haversine does not care -- it wraps -- so `length()` and `along()` stayed correct and nothing ever
 * crashed. But Mapbox measures `line-progress` in projected space, where that single phantom segment
 * is worth about 360 units on a line whose real span is around 50. The reveal was therefore driven
 * almost entirely by the seam, and the drawn line ran far ahead of the marker on every Pacific leg.
 *
 * Coordinates are copied rather than mutated: the input array is the caller's `reelData`.
 */
const convertMultiLineStringToLineString = (multiLine) => {
	const points: number[][] = [];
	let offset = 0;

	multiLine.geometry.coordinates.forEach((line, index) => {
		if (index > 0 && line.length > 0 && points.length > 0) {
			const lonDiff = line[0][0] + offset - points[points.length - 1][0];
			if (Math.abs(lonDiff) > 180) offset += lonDiff > 0 ? -360 : 360;
		}
		line.forEach((coord) => points.push([coord[0] + offset, coord[1]]));
	});

	return lineString(points);
};

//Styles

// const PRIMARY_COLOR = "#FF4252";
const transportIcons = [
	{ name: "planeIcon", icon: Plane },
	{ name: "carIcon", icon: Car },
	{ name: "trainIcon", icon: Train },
	{ name: "busIcon", icon: Bus },
	{ name: "ferryIcon", icon: Ferry },
	// PathSequence builds the image name as `${vehicle}Icon`, so a leg can only show a marker for a
	// mode that is registered here. Walking and cycling were missing, which is why a walked leg fell
	// back to a bare dot.
	{ name: "walkIcon", icon: Walk },
	{ name: "bikeIcon", icon: Bike },
	{ name: "taxiIcon", icon: Taxi },
];

/**
 * What sits inside the destination pin. Without one the pin shows its position number, which is all
 * the reel could ever say about a stop. A crawl wants to say what kind of stop it is.
 */
const venueIcons = [
	{ name: "venue-cocktail", icon: VenueCocktail },
	{ name: "venue-beer", icon: VenueBeer },
	{ name: "venue-wine", icon: VenueWine },
	{ name: "venue-tropical", icon: VenueTropical },
	{ name: "venue-beach", icon: VenueBeach },
	{ name: "venue-waterfall", icon: VenueWaterfall },
	{ name: "venue-viewpoint", icon: VenueViewpoint },
	{ name: "venue-market", icon: VenueMarket },
	// Tryout: one stand-in photo card plus the blurred ellipse that grounds it. Real cards would be
	// one image per destination, composed the same way.
	{ name: "card-shadow", icon: CardShadow },
	{ name: "card-1", icon: Card1 },
	{ name: "card-2", icon: Card2 },
	{ name: "card-3", icon: Card3 },
	{ name: "card-4", icon: Card4 },
	{ name: "card-5", icon: Card5 },
	{ name: "card-6", icon: Card6 },
];

/**
 * A place's own glyph for its pin, drawn from the path its stop carries (placeStops.ts): the web map's
 * marker drawing for that kind of place, in the map's icon ink on a transparent square the size of the
 * bundled venue icons, with the same margin, so the pin layer sizes both alike.
 */
const GLYPH_PX = 256;
type GlyphPart = string | { d: string; fill?: string; stroke?: string; strokeWidth?: string | number };
export const glyphImage = (glyph?: { d?: GlyphPart[]; viewBox?: string } | null): ImageData | null => {
	if (!glyph?.d?.length || typeof document === "undefined") return null;
	const [, , vw = 32, vh = 32] = (glyph.viewBox || "0 0 32 32").split(/[\s,]+/).map(Number);
	const canvas = document.createElement("canvas");
	canvas.width = GLYPH_PX;
	canvas.height = GLYPH_PX;
	const ctx = canvas.getContext("2d");
	if (!ctx || typeof Path2D === "undefined") return null;
	const k = (GLYPH_PX * 0.8) / Math.max(vw, vh);
	ctx.translate((GLYPH_PX - vw * k) / 2, (GLYPH_PX - vh * k) / 2);
	ctx.scale(k, k);
	ctx.fillStyle = MARKER_ICON_COLOR_AGREED;
	ctx.strokeStyle = MARKER_ICON_COLOR_AGREED;
	ctx.lineCap = "round";
	ctx.lineJoin = "round";
	// A part is a filled path, or -- in the line-drawn icons, the cocktail among them -- an object with
	// its own stroke. Filling a line drawing, or handing Path2D the object, draws nothing at all.
	glyph.d.forEach((part) => {
		if (typeof part === "string") return ctx.fill(new Path2D(part));
		if (!part?.d) return;
		const path = new Path2D(part.d);
		if (part.fill && part.fill !== "none") ctx.fill(path);
		if (part.stroke && part.stroke !== "none") {
			ctx.lineWidth = Number(part.strokeWidth) || 2;
			ctx.stroke(path);
		}
	});
	return ctx.getImageData(0, 0, GLYPH_PX, GLYPH_PX);
};
const glyphName = (index: number) => `venue-glyph-${index}`;

mapboxgl.accessToken = "pk.eyJ1Ijoib21hcnMiLCJhIjoiY2w0NDN3OHRvMDAxazNkcXRzcG1kMmpyMCJ9.l6IL4HDt1K5dfcDAFnp-vg";

// Helper to get CSS variable value at runtime (for B2B template support)
const getCssVariableColor = (varName: string, fallback: string): string => {
	if (typeof document !== "undefined") {
		const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
		return value || fallback;
	}
	return fallback;
};

/** The two ways a reel can present a stop's photographs -- see the `photoMode` discussion below. */
export type PhotoMode = "fullscreen" | "cards";

const getDefaultReelEditorStyles = () => ({
	isHD: false,
	mapStyle: "mapbox://styles/omars/clgar7r6s000401pjx0hrkkeg",
	name: "Test6543",
	orientation: "portrait",
	pictureSpeed: 0.8,
	speed: "medium",
	transportMarker: "transport",
	showLogo: true,
	showOutro: true,
	showJournalTitle: false,
	profilePicture: "",
	pictureFit: "cover",
	photoMode: "fullscreen" as PhotoMode,
	// New branding structure
	branding: {
		colors: {
			primary: getCssVariableColor("--brand-primary", DEFAULT_B2B_THEME.BRAND_PRIMARY.toString()),
			secondary: getCssVariableColor("--brand-secondary", DEFAULT_B2B_THEME.BRAND_SECONDARY.toString()),
			primarylight: getCssVariableColor("--brand-primary-light", getLightColor(DEFAULT_B2B_THEME.BRAND_PRIMARY.toString())),
			secondarylight: getCssVariableColor("--brand-secondary-light", getLightColor(DEFAULT_B2B_THEME.BRAND_SECONDARY.toString())),
		},
		logo: null,
		squareLogo: null,
		companyName: "Stippl",
		isCustomBranded: false,
	},
	brandLogo: "",
});

// "https://media.stippl.io/633c063659de593317854e06.jpeg?1702557066646",

/**
 * Frames per kilometre of route, and the ceiling on a leg's duration -- of which a 2.25th is also
 * the FLOOR, and the floor is what actually decides a city film's length. A walk of 0.6 km times
 * even the slowest rate is under a frame, so every city leg lands on the floor; frames-per-kilometre
 * only takes over once a leg is tens of kilometres long.
 *
 * `fast`, `medium` and `slow` are the three values a user has always been able to pick in
 * VideoCreateModal, and they are what gets persisted per-reel in Mongo (`models/ReelStyle.ts`,
 * `medium` the fallback everywhere a stored style is missing one). They keep their original
 * timing here rather than sliding down a rung, or every reel anyone has ever saved would silently
 * re-time itself the next time it renders. `superfast` and `superslow` are new -- they extend the
 * same ladder at its two ends rather than displacing the names already in use.
 */
const SPEEDS: Record<string, { perKm: number; maxDuration: number }> = {
	superfast: { perKm: 0.005, maxDuration: MAX_MAP_DURATION * 0.25 },
	fast: { perKm: 0.02, maxDuration: MAX_MAP_DURATION * 0.5 },
	medium: { perKm: 0.08, maxDuration: MAX_MAP_DURATION },
	slow: { perKm: 0.3, maxDuration: MAX_MAP_DURATION * 1.5 },
	superslow: { perKm: 0.6, maxDuration: MAX_MAP_DURATION * 2.5 },
};

export const getLineSpeed = (speed) => (SPEEDS[speed] ?? SPEEDS.medium).perKm;

export const getMaxLineDuration = (speed) => (SPEEDS[speed] ?? SPEEDS.medium).maxDuration;

/**
 * A leg's drawn geometry. Two points is a flight, so a great circle; more is a road, so a spline.
 * Both the component, the altitude plan and `calculateMetaData` need this, and when they each built
 * it themselves they drifted apart -- which is how the map source and PathSequence ended up drawing
 * two different lines across the antimeridian.
 */
export const buildLegPath = (destination: any): any => {
	const coords = destination?.path;
	if (!(coords?.length > 0)) return null;
	let path: any =
		coords.length === 2
			? greatCircle(point([coords[0][0], coords[0][1]]), point([coords[1][0], coords[1][1]]))
			: bezierSpline(lineString(coords));
	if (path?.geometry?.type === "MultiLineString") path = convertMultiLineStringToLineString(path);
	return path;
};

/** How long a leg would last on distance alone -- before the altitude plan asks for more. */
export const legBaseFrames = (reelData: any[], speed: any): number[] =>
	(reelData || []).map((destination: any, index: number) => {
		const path = buildLegPath(destination);
		// A destination with no path is the end of the trip, not a leg of zero length. Without this it
		// still gets the formula's floor -- 36 frames -- which calculateMetaData counts and the Series
		// rightly does not draw, leaving that many frames of bare map after the outro has finished.
		// The last destination is the end of the trip whatever its path says: the render worker gives
		// it a two-point stand-in for its coordinate checks, and that stand-in collected the floor too.
		if (!path?.geometry || index === reelData.length - 1) return 0;
		//@ts-ignore
		const routeDistance = length(path);
		const frames = Math.round(
			Math.min(Math.max(Math.floor(routeDistance * getLineSpeed(speed)), getMaxLineDuration(speed) / 2.25), getMaxLineDuration(speed))
		);
		return isNaN(frames) ? 0 : frames;
	});

export const getPhotoDuration = (duration) => {
	return duration * FPS;
};

/**
 * Which of the two ways of showing a stop's photographs a film uses. The component, the film's length
 * and its segment layout all have to make the same choice, so they ask here.
 */
const photoModeOf = (styles: any): PhotoMode => (styles as { photoMode?: PhotoMode }).photoMode ?? "fullscreen";

/**
 * The plan for every leg of a film: how high the camera flies and how many frames the leg lasts. The
 * plan decides how long a leg is, and therefore where everything after it starts, so the component,
 * `calculateReelDuration` and `reelSegments` all ask this one function. When they each built it, any
 * change to how a leg is timed silently desynced the composition's length from what it draws.
 */
const planReelLegs = (reelData: any[], styles: any) =>
	planLegs(reelData, {
		baseFrames: legBaseFrames(reelData, styles.speed),
		pitchDeg: START_PITCH,
		fovTanHalf: FOV_TAN_HALF,
		fps: FPS,
		speed: styles.speed,
		zoom: (styles as { cameraZoom?: number }).cameraZoom,
	});

export const CompositionComponent = ({
	currentTrip,
	reelData,
	styles = getDefaultReelEditorStyles(),
	precomputedPaths = null,
	reelSegments: filmSegments = null,
	warmupRange = null,
}: {
	currentTrip: any;
	reelData: any[];
	styles?: ReturnType<typeof getDefaultReelEditorStyles>;
	precomputedPaths?: any[] | null;
	/**
	 * The film's segment layout (`reelSegments`), added to the props by `calculateMetaData`. A render
	 * that cuts the film into frame ranges needs it to know what a tab that starts partway in has missed.
	 */
	reelSegments?: ReelSegment[] | null;
	/**
	 * The frames this tab renders, first and last inclusive and absolute. The render worker sets it per
	 * chunk; the tile warm-up then walks only the part of the route those frames can see, instead of the
	 * whole film in every tab. Without it, or without `reelSegments`, the warm-up walks the whole route.
	 */
	warmupRange?: [number, number] | null;
}) => {
	const htmlElement = document.querySelector("#userPicture"); // Or use another selector method

	Poppins.loadFont();
	Rubik.loadFont();
	const scale = styles.isHD ? 1 : 0.5;
	const introStyle = introStyleOf(styles);
	const INTRO_FRAMES = introFrames(styles);

	// Photo cards are a variant, not the new default. Off unless a caller asks for them, so the
	// existing look -- numbered pins, photos full-screen between destinations via PhotoSequence --
	// stays exactly as it was. Declared here because the layer-building effect below needs it.
	//
	// Fullscreen and cards are two ways of showing the SAME pictures, not two features that can
	// stack. With both on, a six-stop crawl spent 15 of its 36 seconds on stills it had already
	// shown on the map, and the walk -- the thing this format is for -- became the interruption. One
	// source per stop, one presentation -- but which one is the caller's explicit choice (`photoMode`)
	// rather than something derived from a single "cards on" boolean.
	const photoMode = photoModeOf(styles);
	const cardSettings = styles as { cardStagger?: number; cardStiffness?: number };
	const showPhotoCards = photoMode === "cards";
	const showFullscreenPhotos = photoMode === "fullscreen";
	// The intro used to fall from 32,000 km to 250,000 m for every trip, which is a fine way to arrive
	// in a country and a strange way to arrive on an island: you cross the whole planet and then still
	// have to descend two more orders of magnitude before the first stop. Scale it to where the trip
	// actually is -- a local loop now starts at a regional view, a long-haul trip still starts at the
	// globe, and in both cases the intro hands the camera over at the height the first leg wants.
	const legPlans = planReelLegs(reelData, styles);
	const introEndAltitude = legPlans[0]?.entry || START_ALTITUDE;
	const widestLeg = legPlans.length ? Math.max(...legPlans.map((p) => p.own)) : 0;
	const introStartAltitude = Math.max(200000, Math.min(32000000, Math.max(introEndAltitude * 60, widestLeg * 4)));
	const ref = useRef<HTMLDivElement>(null);
	const [map, setMap] = useState<Map | null>(null);
	// The geometry PathSequence is handed for each leg, once, for FinishedSequences: what a finished leg leaves on
	// the map is worked out from the same line the leg was drawn from.
	const legPaths = useMemo(() => (filmSegments ? (reelData || []).map((destination: any) => buildLegPath(destination)) : null), [filmSegments, reelData]);

	// Get colors with backwards compatibility
	const destinationColor = styles.branding?.colors?.primary || DEFAULT_B2B_THEME.BRAND_PRIMARY;
	const transportColor = styles.branding?.colors?.secondary || DEFAULT_B2B_THEME.BRAND_SECONDARY;

	const [handle] = useState(() => delayRender("Loading map..."));

	// Use pre-computed paths from worker if available, otherwise compute locally (for preview)
	//
	// The MultiLineString flattening has to happen HERE, not only where PathSequence gets its prop.
	// These features are what `source-${index}` is fed, and `line-progress` runs 0..1 *per sub-line*
	// on a MultiLineString -- so an antimeridian crossing had both halves revealing at once, and the
	// Hawaii half was already inked while the marker was still leaving Sydney. Feeding the map and
	// PathSequence two different geometries is what made the marker look out of step with its line.
	// A template's stops, and a city trip's places, are visited on a day rather than stayed at for nights:
	// their photos say which day.
	const dayBadges = Boolean(currentTrip?.isTemplate || currentTrip?.stops === "places");

	const paths = (
		precomputedPaths ||
		reelData?.map((item) => {
			return item?.path?.length > 0
				? item?.path?.length === 2
					? greatCircle(point([item?.path?.[0]?.[0], item?.path?.[0]?.[1]]), point([item?.path?.[1]?.[0], item?.path?.[1]?.[1]]))
					: bezierSpline(lineString(item?.path))
				: null;
		})
	)?.map((path: any) => (path?.geometry?.type === "MultiLineString" ? convertMultiLineStringToLineString(path) : path));

	const mapDestinationFeatures = currentTrip?.destinations.map((item, index) => {
		const feature = {
			type: "Feature",
			geometry: {
				type: "Point",
				coordinates: getGeocodes(item),
			},
			properties: {
				id: getItemID(item),
				index: index,
				city: styles.showJournalTitle ? reelData?.[index]?.journalTitle || item?.cityEnglish : item?.cityEnglish || item?.normalised_city,
				venueIcon: item?.venueGlyph ? glyphName(index) : item?.venueIcon || "",
			},
		};
		return feature;
	});

	useEffect(() => {
		const _map = new Map({
			container: "map",
			style: styles.mapStyle,
			interactive: true,
			zoom: 0,
			fadeDuration: 0,
			center: [0, 0],
			//@ts-ignore
			projection: "globe",
			// Mapbox sizes its tile cache to a few screenfuls, which is right for a map someone pans and
			// wrong for a film whose route is walked once ahead of time (the warm-up below) and then
			// again frame by frame: with the default, the start of the route was evicted before the
			// render got back to it. Big enough for a long-haul trip; vector tiles are small.
			maxTileCacheSize: 2500,
		});

		_map.loadImage(mapChip, (error, image) => {
			if (error) throw error;
			_map.addImage("mapChip", image, { sdf: true });
		});

		{
			// Drawn here, synchronously, so each glyph exists before the pin layer that names it.
			(currentTrip?.destinations || []).forEach((item: any, index: number) => {
				const image = glyphImage(item?.venueGlyph);
				if (image) _map.addImage(glyphName(index), image);
			});
			[...transportIcons, ...venueIcons].map((icon) =>
				_map.loadImage(icon.icon, (error, image) => {
					if (error) throw error;
					// sdf: false - display icons with their original colors (not colorized by icon-color)
					_map.addImage(icon.name, image, { sdf: false });
				})
			);
		}

		const transportDot = {
			type: "Feature",
			geometry: {
				type: "Point",
				coordinates: paths?.[0].geometry.coordinates?.[0],
			},
			properties: {},
		};

		_map.on("style.load", () => {
			// A raster tile fades in over `raster-fade-duration`, 300 ms of wall clock by default -- the
			// same kind of clock `fadeDuration: 0` above already takes away from labels. The tile gate
			// hands a frame over once its tiles are loaded, so a tile that arrived for that frame was
			// captured at the start of its fade: black squares in the satellite. One tab rarely showed
			// it, because the warm-up had fetched most tiles long before; a frame rendered in another
			// tab, six frames ahead of the last one there, needs fresh tiles far more often.
			try {
				(_map.getStyle()?.layers || [])
					.filter((layer: any) => layer.type === "raster")
					.forEach((layer: any) => _map.setPaintProperty(layer.id, "raster-fade-duration", 0));
			} catch (e: any) {
				console.warn("[TripReel] could not switch off raster fades:", e?.message || e);
			}
			// The same clock again, for everything the sequences set: every `setPaintProperty` eases to
			// its new value over the style's `transition`, 300 ms of wall clock unless the style says
			// otherwise. The route line's opacity, each marker's paint, the transport dot -- all set per
			// frame, all drawn part of the way there, by an amount that depends on how fast the frames
			// before it happened to render. A leg's line lingered four frames into the next leg in one
			// render and not at all in a render that started there. Remotion's own rule for rendering
			// in several tabs is that a frame depends on its number and nothing else.
			try {
				(_map as any).style?.setTransition?.({ duration: 0, delay: 0 });
			} catch (e: any) {
				console.warn("[TripReel] could not switch off style transitions:", e?.message || e);
			}

			// Mapbox Standard (v3) exposes a `basemap` config: lightPreset dawn/day/dusk/night, plus
			// toggles for 3D buildings, labels and landmarks. It only applies to Standard-family
			// styles, so guard it -- calling setConfigProperty on a classic style throws.
			// Optional, so the editor's own styles object does not have to carry them, and cast because
			// setConfigProperty is missing from the mapbox-gl 3.2 typings even though it ships in v3.
			const basemap = styles as {
				lightPreset?: "dawn" | "day" | "dusk" | "night";
				show3dObjects?: boolean;
				showPointOfInterestLabels?: boolean;
				terrainExaggeration?: number;
				/**
				 * Anything else Standard exposes, passed straight through. It publishes 45 configurable
				 * properties -- colorLand, colorBuildings, colorWater, font and the label toggles among
				 * them -- and naming each one here means the next colour we want to set is a code change
				 * rather than a prop.
				 */
				basemapConfig?: Record<string, string | number | boolean>;
			};

			// Real elevation. Standard carries some terrain at mid zoom, but on an island it renders as
			// a flat green shape -- Koh Phangan tops out at 635 m and you could not tell. Adding the DEM
			// source explicitly is what makes a mountainous destination read as one.
			if (basemap.terrainExaggeration) {
				try {
					if (!_map.getSource("mapbox-dem")) {
						_map.addSource("mapbox-dem", {
							type: "raster-dem",
							url: "mapbox://mapbox.mapbox-terrain-dem-v1",
							tileSize: 512,
							maxzoom: 14,
						});
					}
					_map.setTerrain({ source: "mapbox-dem", exaggeration: basemap.terrainExaggeration });
				} catch (e: any) {
					console.warn("[TripReel] terrain not available:", e?.message || e);
				}
			} else {
				// No relief asked for: keep the style's own, but no steeper than RELIEF_CAP.
				try {
					const capped = cappedTerrain((_map as any).getTerrain?.());
					if (capped) _map.setTerrain(capped);
				} catch (e: any) {
					console.warn("[TripReel] terrain not available:", e?.message || e);
				}
			}
			if (basemap.lightPreset) {
				try {
					(_map as any).setConfigProperty("basemap", "lightPreset", basemap.lightPreset);
					if (basemap.show3dObjects !== undefined) (_map as any).setConfigProperty("basemap", "show3dObjects", basemap.show3dObjects);
					// Mapbox's own POI icons compete with the destination pins -- on the Bangkok crawl the
					// bars were lost in a field of unrelated restaurant and shop markers.
					if (basemap.showPointOfInterestLabels !== undefined)
						(_map as any).setConfigProperty("basemap", "showPointOfInterestLabels", basemap.showPointOfInterestLabels);
					Object.entries(basemap.basemapConfig || {}).forEach(([key, value]) => {
						try {
							(_map as any).setConfigProperty("basemap", key, value);
						} catch (e: any) {
							// One unknown key should not cost the rest of the palette.
							console.warn(`[TripReel] basemap config "${key}" rejected:`, e?.message || e);
						}
					});
				} catch (e: any) {
					console.warn("[TripReel] lightPreset not supported by this style:", e?.message || e);
				}
			}
			_map.addSource(`transportMarkerSource`, {
				type: "geojson",
				//@ts-ignore
				data: transportDot,
				lineMetrics: true,
			});
			_map.addLayer({
				type: "circle",
				source: `transportMarkerSource`,
				id: `transportMarkerDot`,
				paint: {
					// "circle-color": styles.TRANSPORT_MARKER_COLOR,
					"circle-color": transportColor,
					"circle-radius": 24 * scale,
					"circle-opacity": 0,
				},
			});

			_map.addLayer({
				type: "symbol",
				source: `transportMarkerSource`,
				id: `transportMarkerIcon`,
				layout: {
					"icon-image": "",
					"icon-allow-overlap": true,
					"text-allow-overlap": true,
					"icon-size": 0.875 * scale,
				},
				paint: {
					"icon-opacity": 0,
					// "icon-color": styles.TRANSPORT_ICON_COLOR,
					"icon-color": transportColor,
				},
			});

			////////

			_map.addLayer({
				type: "symbol",
				source: `transportMarkerSource`,
				id: `transportProfilePicture`,
				layout: {
					"icon-image": "UserBadge",
					"icon-allow-overlap": true,
					"text-allow-overlap": true,
					"icon-size": 1,
				},
				paint: {
					"icon-opacity": 0,
				},
			});
			paths?.map(
				(path, index) => (
					_map.addSource(`source-${index}`, {
						type: "geojson",
						data: path,
						lineMetrics: true,
					}),
					_map.addLayer({
						type: "line",
						source: `source-${index}`,
						id: `line-${index}`,
						paint: {
							// "line-color": styles.TRANSPORT_PATH_COLOR,
							"line-color": transportColor,
							"line-width": 6 * scale,
							"line-opacity": 0,
						},
					})
				)
			);
			// Fetch each stop's photographs and draw them into the card shape before any layer asks for
			// them. Remotion has to wait: an image that arrives a frame late is simply absent from that
			// frame, and a render is not a browser that can repaint. A photo that fails -- an expired
			// CDN link, a host that refuses the request -- just leaves that card on the bundled pool.
			if (showPhotoCards) {
				const cardHandle = delayRender("Drawing photo cards");
				const jobs: Promise<void>[] = [];
				(mapDestinationFeatures || []).forEach((_m: any, index: number) => {
					stopPhotos(reelData?.[index], CARDS_PER_STOP).forEach((url: string, card: number) => {
						jobs.push(
							new Promise<void>((resolve) => {
								const img = new Image();
								img.crossOrigin = "anonymous";
								img.onload = () => {
									try {
										const data = drawPhotoCard(img);
										if (data) {
											const name = cardImageName(index, card);
											if (!_map.hasImage(name)) _map.addImage(name, data);
											if (_map.getLayer(`cardPhoto-${index}-${card}`)) {
												_map.setLayoutProperty(`cardPhoto-${index}-${card}`, "icon-image", name);
											}
										}
									} catch {
										// Tainted canvas or an image the browser will not decode; the pool covers it.
									}
									resolve();
								};
								img.onerror = () => resolve();
								img.src = url;
							})
						);
					});
				});
				Promise.all(jobs).then(() => continueRender(cardHandle));
			}

			mapDestinationFeatures?.map((marker, index) => {
				_map.addSource(`markerSource-${index}`, {
					type: "geojson",
					data: marker,
					lineMetrics: true,
				});
				//Green marker pulse
				_map.addLayer({
					type: "circle",
					source: `markerSource-${index}`,
					id: `pulse-${index}`,
					paint: {
						// "circle-color": styles.MARKER_PULSE_COLOR,
						"circle-color": destinationColor,
						"circle-radius": 24 * scale,
						"circle-opacity": 0,
					},
				});
				if (showPhotoCards) {
					// The fan.
					//
					// One layer per card, because each needs its own rotation, its own screen offset and its
					// own spring delay. `icon-pitch-alignment: map` lays the shadow flat in the ground plane
					// so it foreshortens with the camera; `viewport` keeps the cards standing up facing it.
					// Flat shadow under upright cards is the whole depth trick -- no model, no perspective
					// baked into the images.
					//
					// Rotation alignment is viewport, not map: `icon-offset` rotates with the symbol, so a
					// map-aligned card swings its own offset around and lands beside the pin instead of above
					// it. The lift is `icon-translate` in screen pixels for the same reason.
					_map.addLayer({
						type: "symbol",
						source: `markerSource-${index}`,
						id: `cardShadow-${index}`,
						layout: {
							"icon-image": "card-shadow",
							"icon-size": 0.85 * scale,
							"icon-pitch-alignment": "map",
							"icon-rotation-alignment": "map",
							"icon-allow-overlap": true,
							"icon-ignore-placement": true,
						},
						paint: { "icon-opacity": 0 },
					});
					// One card per photo this stop actually has, not three regardless. A stop with a single
					// picture fans one card, and a stop with none gets no cards.
					const photos = stopPhotos(reelData?.[index], CARDS_PER_STOP);
					// No stock fallback, not even when the whole trip has no photos: that exception still
					// fanned three bundled photographs -- beaches and palm trees -- over every stop, and on a
					// city trip whose places have no pictures it put them on a brown cafe in Amsterdam. A
					// stock photo under a place's name reads as a claim about the place; nothing is better.
					const cardCount = photos.length;
					Array.from({ length: cardCount }).forEach((_, card) => {
						_map.addLayer({
							type: "symbol",
							source: `markerSource-${index}`,
							id: `cardPhoto-${index}-${card}`,
							layout: {
								// The stop's own photograph when there is one; otherwise walk the bundled pool
								// so neighbouring stops without pictures do not show the same card.
								// Always start on the bundled pool. A photo that loads switches this layer over
								// once it is drawn; one that does not -- a host with no CORS header, an expired
								// link -- simply leaves the card as it is. Pointing at the photo up front made a
								// failure invisible: the layer referenced an image that never arrived and the
								// whole fan vanished instead of falling back.
								"icon-image": `card-${((index * CARDS_PER_STOP + card) % CARD_POOL) + 1}`,
								"icon-size": 0.85 * scale,
								"icon-anchor": "bottom",
								"icon-pitch-alignment": "viewport",
								"icon-rotation-alignment": "viewport",
								"icon-allow-overlap": true,
								"icon-ignore-placement": true,
							},
							paint: {
								"icon-opacity": 0,
								"icon-translate": [0, -132 * scale],
								"icon-translate-anchor": "viewport",
							},
						});
					});
				}
				//Destination name
				_map.addLayer({
					type: "symbol",
					source: `markerSource-${index}`,
					id: `destinationName-${index}`,
					layout: {
						"text-field": `${marker.properties.city}`,
						"text-font": ["Poppins SemiBold"],
						"text-size": (showPhotoCards ? 46 : 38) * scale,
						"icon-image": "mapChip",
						"icon-text-fit": "both",
						"icon-text-fit-padding": [16 * scale, 32 * scale, 16 * scale, 32 * scale],
						"icon-allow-overlap": true,
						"text-allow-overlap": true,
					},
					paint: {
						// "text-color": styles.DESTINATION_TEXT_COLOR,
						// "icon-color": styles.DESTINATION_COLOR,
						"text-color": "#FFF",
						"icon-color": destinationColor,
						"text-opacity": 0,
						"icon-opacity": 0,
						// Well clear under the dot: the circle is 32px in radius, so 62 still clipped its
						// bottom edge and the label read as sitting on the pin rather than under it.
						// The pill is two lines at 46px, so about 142 tall. Its bottom edge has to clear the
						// 32px-radius dot, which puts its centre at least 103 up; -120 leaves a real gap.
						// The top then overlaps the cards, which is the intent -- it ties the two together.
						"icon-translate": [0, -120 * scale],
						"text-translate": [0, -120 * scale],
						"icon-translate-anchor": "viewport",
						"text-translate-anchor": "viewport",
					},
				});
				//White marker circle
				_map.addLayer({
					type: "circle",
					source: `markerSource-${index}`,
					id: `marker-${index}`,
					paint: {
						// "circle-color": styles.MARKER_COLOR,
						"circle-color": "#FFF",
						"circle-radius": 32 * scale,
						"circle-opacity": 0,
					},
				});
				//Number in marker circle
				_map.addLayer({
					type: "symbol",
					source: `markerSource-${index}`,
					id: `countText-${index}`,
					layout: {
						"text-field": marker.properties.venueIcon ? "" : `${index + 1}`,
						"text-font": ["Poppins Bold"],
						"text-size": 36 * scale,
						"icon-image": marker.properties.venueIcon || "",
						"icon-size": 0.16 * scale,
						"icon-allow-overlap": true,
						"text-allow-overlap": true,
					},
					paint: {
						// "text-color": styles.MARKER_TEXT_COLOR,
						"text-color": destinationColor,
						"text-opacity": 0,
						"icon-opacity": 0,
					},
				});
			});
		});

		_map.on("load", () => {
			// The container has no size yet when the map is constructed, so Mapbox falls back to 400x300
			// and only corrects itself when its ResizeObserver fires, a frame later. Anything that reads
			// the transform before then -- the tile warm-up below, anything that projects a coordinate
			// to the screen -- is working in a 400x300 viewport. Measured: the intro's route overlay
			// landed in the top-left corner at a ninth of its size.
			_map.resize();
			// Mapbox v3 lights every layer with the style's light, ours included, so under a dusk or night
			// preset the route, the stop dots and their numbers came out dimmed to grey. Emissive strength 1
			// is the style spec's way of saying "this layer gives its own light": it keeps the reel's marks
			// at full colour whatever the time of day. Only our own sources; the basemap keeps its lighting.
			if ((styles as { lightPreset?: string }).lightPreset) {
				const ours = (source: any) => typeof source === "string" && /^(source-|markerSource-|transportMarkerSource)/.test(source);
				(_map.getStyle()?.layers || []).forEach((layer: any) => {
					if (!ours(layer.source)) return;
					const props =
						layer.type === "line"
							? ["line-emissive-strength"]
							: layer.type === "circle"
							? ["circle-emissive-strength"]
							: layer.type === "symbol"
							? ["icon-emissive-strength", "text-emissive-strength"]
							: [];
					props.forEach((prop) => {
						try {
							_map.setPaintProperty(layer.id, prop as any, 1);
						} catch {
							// A GL JS without the property draws the layer as before.
						}
					});
				});
			}
			const start = () => {
				continueRender(handle);
				setMap(_map);
			};
			// Only a render pays the per-frame tile round trip that the warm-up exists to batch; the
			// editor's Player streams frames interactively and its delayRender budget is 30 s, which a
			// long trip's warm-up would overrun.
			if (!getRemotionEnvironment().isRendering) {
				start();
				return;
			}
			// A tab that renders one chunk of the film only needs the part of the route its own frames fly
			// over. Warmed in every tab, the whole route's tiles were fetched once per tab, each against
			// its own 90 s budget. Both inputs are needed: the range says which frames, the segments say
			// where on the route those frames are.
			const warmupFrames = warmupRange && filmSegments ? warmupRange : null;
			// A clip intro flies its own path -- a country overview, a zoom out, the globe -- and a render tab
			// that starts partway into it jumps there cold. Walk that path first, from frame 0, which for
			// the styles that open on the map is also the thumbnail. A chunk that starts after the intro and
			// the frames its overlay lingers into the first stop (`introOverhang`) never sees any of it.
			const introPath =
				introStyle === "classic" || (warmupFrames !== null && warmupFrames[0] >= INTRO_FRAMES + introOverhang(introStyle))
					? []
					: introWarmupKeyframes({
							style: introStyle,
							config: (styles as any).intro,
							introFrames: INTRO_FRAMES,
							stop: { lng: currentTrip?.destinations?.[0]?.longitude, lat: currentTrip?.destinations?.[0]?.latitude },
							endAltitude: introEndAltitude,
							endPitch: START_PITCH,
							endBearing: legHeadingAt(reelData?.[0]?.path, 0),
							width: _map.getContainer().clientWidth,
							height: _map.getContainer().clientHeight,
							scale,
							geometry: introGeometry({ style: introStyle, paths, reelData, currentTrip }),
							fit: (bounds, padding) => _map.cameraForBounds(bounds, { padding }),
					  });
			const planned = [
				...introPath,
				...planWarmupKeyframes({
					reelData,
					legPlans,
					paths,
					introStartAltitude,
					introEndAltitude,
					startPitch: START_PITCH,
					// Only to tell each view which frame of the film it belongs to; nothing else changes.
					...(warmupFrames && filmSegments ? { introFrames: INTRO_FRAMES, legStarts: legStartFrames(filmSegments, reelData.length) } : {}),
				}),
			];
			const keyframes = warmupFrames ? keyframesInRange(planned, warmupFrames) : planned;
			warmTiles(_map, keyframes)
				.then((result) => {
					const range = warmupFrames ? `, frames ${warmupFrames[0]}-${warmupFrames[1]} (${keyframes.length} of ${planned.length} planned views)` : "";
					console.log(`[reel] tile warm-up: ${result.visited} views, ${result.timedOut} timed out, ${(result.elapsedMs / 1000).toFixed(1)} s${range}`);
				})
				.catch(() => undefined)
				.then(start);
		});
	}, [handle, styles.mapStyle, styles.profilePicture, showPhotoCards]);

	//Update style
	// useEffect(() => {
	//   mapDestinationFeatures?.map((marker, index) => {
	//     //destination name background color
	//     map?.setPaintProperty(`destinationName-${index}`, "icon-color", styles.DESTINATION_COLOR);
	//     //destination name text color
	//     map?.setPaintProperty(`destinationName-${index}`, "text-color", styles.DESTINATION_TEXT_COLOR);
	//     //Marker color
	//     map?.setPaintProperty(`marker-${index}`, "circle-color", styles.MARKER_COLOR);
	//     //Pulse color
	//     map?.setPaintProperty(`pulse-${index}`, "circle-color", styles.MARKER_PULSE_COLOR);
	//     //Marker text color
	//     map?.setPaintProperty(`countText-${index}`, "text-color", styles.MARKER_TEXT_COLOR);
	//   });
	//   //Transport Marker color
	//   map?.setPaintProperty(`transportMarkerDot`, "circle-color", styles.TRANSPORT_MARKER_COLOR);
	//   //Transport Icon color
	//   map?.setPaintProperty(`transportMarkerIcon`, "icon-color", styles.TRANSPORT_ICON_COLOR);
	// }, [styles]);

	useEffect(() => {
		mapDestinationFeatures?.map((marker, index) => {
			//destination name background color
			map?.setPaintProperty(`destinationName-${index}`, "icon-color", destinationColor);
			//destination name text color
			map?.setPaintProperty(`destinationName-${index}`, "text-color", "#FFF");
			//Marker color
			map?.setPaintProperty(`marker-${index}`, "circle-color", "#FFF");
			//Pulse color
			map?.setPaintProperty(`pulse-${index}`, "circle-color", destinationColor);
			//Marker text color
			map?.setPaintProperty(`countText-${index}`, "text-color", destinationColor);
		});
		//Transport Marker color
		map?.setPaintProperty(`transportMarkerDot`, "circle-color", transportColor);
		//Transport Icon color
		map?.setPaintProperty(`transportMarkerIcon`, "icon-color", transportColor);
	}, [styles]);

	useEffect(() => {
		if (htmlElement && styles.profilePicture && styles.transportMarker === "avatar") {
			//@ts-ignore
			html2canvas(htmlElement, { useCORS: true, backgroundColor: null }).then((canvas) => {
				const imgUrl = canvas.toDataURL("image/png");
				console.log(imgUrl);
				if (imgUrl) {
					map?.loadImage(imgUrl, (error, image) => {
						if (error) throw error;
						console.log("HELLO", image);
						map?.addImage("UserBadge", image);
					});
				}

				map?.setLayoutProperty("transportMarkerIcon", "icon-image", "UserBadge");
			});
		}
	}, [htmlElement, map, styles]);

	const renderLogo = () => {
		const { logo, squareLogo, companyName, isCustomBranded } = styles.branding || {};
		// Adjust bottom spacing for landscape orientation (push logo closer to edge)
		const isLandscape = styles.orientation === "landscape";
		const logoBottomSpacing = isLandscape ? 16 * scale : 64 * scale;

		// Logo fallback chain: logo → squareLogo → company name text badge → Stippl logo
		if (isCustomBranded) {
			// B2B branded videos
			if (logo) {
				// Use full company logo (preferred)
				return (
					<AbsoluteFill>
						<div className="stippl-logo-container" style={{ transform: `translate(-50%, 0) scale(${scale})`, bottom: logoBottomSpacing }}>
							<div className="stippl-logo brand-logo">
								<img src={logo} alt="Brand logo" />
							</div>
							<div className="poweredby">
								<img src={PoweredBy} alt="poweredBy" />
							</div>
						</div>
					</AbsoluteFill>
				);
			} else if (squareLogo) {
				// Use square logo (fallback)
				return (
					<AbsoluteFill>
						<div className="stippl-logo-container" style={{ transform: `translate(-50%, 0) scale(${scale})`, bottom: logoBottomSpacing }}>
							<div className="stippl-logo brand-logo">
								<img src={squareLogo} alt="Brand logo" />
							</div>
							<div className="poweredby">
								<img src={PoweredBy} alt="poweredBy" />
							</div>
						</div>
					</AbsoluteFill>
				);
			} else if (companyName && companyName !== "Stippl") {
				// Use company name text badge (fallback)
				return (
					<AbsoluteFill>
						<div className="stippl-logo-container" style={{ transform: `translate(-50%, 0) scale(${scale})`, bottom: logoBottomSpacing }}>
							<div style={{ transform: `scale(${scale})`, transformOrigin: "bottom" }}>
								<TextTag text={companyName} color="black" />
							</div>
						</div>
					</AbsoluteFill>
				);
			}
		}

		// Default: Show Stippl logo (B2C or no custom branding)
		return (
			<AbsoluteFill>
				<div className="stippl-logo-container" style={{ bottom: logoBottomSpacing }}>
					<div className="stippl-logo" style={{ transform: `scale(${scale})`, transformOrigin: "bottom" }}>
						<img src={StipplLogo} alt="Brand logo" />
					</div>
				</div>
			</AbsoluteFill>
		);
	};

	// How the fan comes in. Taste, not physics, so both are props: frames between one card and the
	// next, and the spring stiffness that decides how briskly each one lands.
	const cardStagger = cardSettings.cardStagger ?? 5;
	const cardStiffness = cardSettings.cardStiffness ?? 260;

	return (
		<>
			{styles.profilePicture && (
				<div
					id="userPicture"
					style={{
						zIndex: -1,
						width: styles.isHD ? 128 : 64,
						height: styles.isHD ? 128 : 64,
						borderRadius: "50%",
						overflow: "hidden",
						background: "transparent",
						border: "2px solid white",
					}}
				>
					<img src={styles.profilePicture} style={{ width: "100%", height: "100%", borderRadius: "50%" }} />
				</div>
			)}
			<AbsoluteFill ref={ref} id="map" />

			{/* Before every sequence on purpose. It writes to the map in its render body, and so do the
			    sequences after it: where one of them is mounted it writes last and wins, and this only speaks
			    for the legs and stops a tab that started partway into the film never ran. */}
			<FinishedSequences
				map={map}
				reelSegments={filmSegments}
				reelData={reelData}
				legPaths={legPaths}
				PATH_COLOR={transportColor as string}
				scale={scale}
				showPhotoCards={showPhotoCards}
				cardStagger={cardStagger}
				cardStiffness={cardStiffness}
			/>

			{introStyle === "classic" ? (
				<Sequence from={0} durationInFrames={INTRO_FRAMES}>
					<IntroSequence
						map={map}
						currentTrip={currentTrip}
						INTRO_DURATION={INTRO_FRAMES}
						targetLngLat={{
							lng: currentTrip?.destinations?.[0]?.longitude,
							lat: currentTrip?.destinations?.[0]?.latitude,
						}}
						startAltitude={introStartAltitude}
						endAltitude={introEndAltitude}
						startBearing={0}
						endBearing={bearing(first(reelData?.[0]?.path), last(reelData?.[0]?.path))}
						startPitch={0}
						endPitch={START_PITCH}
						scale={scale}
					/>
				</Sequence>
			) : null}
			<Sequence from={INTRO_FRAMES} durationInFrames={MARKER_DURATION}>
				<MarkerSequence
					index={0}
					map={map}
					MARKER_DURATION={MARKER_DURATION}
					reelData={reelData}
					currentTrip={currentTrip}
					startAltitude={introEndAltitude}
					endAltitude={introEndAltitude}
					startPitch={START_PITCH}
					showPhotoCards={showPhotoCards}
					cardStagger={cardStagger}
					cardStiffness={cardStiffness}
					scale={scale}
				/>
			</Sequence>

			{/* Skipped entirely when the map fan is showing these same pictures -- not given a length of
			    nought, because Remotion refuses that, the same rule that makes "no outro" render one
			    transparent frame rather than nothing. */}
			{showFullscreenPhotos && reelData[0].photos?.length > 0 ? (
				<Sequence from={INTRO_FRAMES + MARKER_DURATION} durationInFrames={getPhotoDuration(styles.pictureSpeed) * reelData[0].photos.length}>
					<PhotoSequence
						photos={reelData[0]?.photos}
						city={reelData[0]?.city}
						nights={reelData[0]?.duration}
						journalTitle={reelData[0]?.journalTitle}
						PHOTO_DURATION={getPhotoDuration(styles.pictureSpeed)}
						showJournalTitle={styles.showJournalTitle}
						scale={scale}
						objectFit={styles.pictureFit}
						isTemplate={dayBadges}
						dayIndex={reelData[0]?.firstDayNumber ?? 1}
						lastDayIndex={reelData[0]?.lastDayNumber}
						isLandscape={styles.orientation === "landscape"}
					/>
				</Sequence>
			) : null}
			{renderLogo()}
			{introStyle !== "classic" ? (
				// After the stop, not before it: the intro's pin is still on screen for the first frames of the
				// stop, landing on the marker, and has to be drawn over it.
				<Sequence from={0} durationInFrames={INTRO_FRAMES + introOverhang(introStyle)}>
					<ClipIntro
						map={map}
						style={introStyle}
						config={(styles as any).intro}
						introFrames={INTRO_FRAMES}
						currentTrip={currentTrip}
						paths={paths}
						reelData={reelData}
						stop={{ lng: currentTrip?.destinations?.[0]?.longitude, lat: currentTrip?.destinations?.[0]?.latitude }}
						endAltitude={introEndAltitude}
						endPitch={START_PITCH}
						// The heading MarkerSequence starts its first stop on. The classic intro ends on the leg's
						// chord instead, which is a different number, so its hand-over turns in one frame.
						endBearing={legHeadingAt(reelData?.[0]?.path, 0)}
						brandColor={destinationColor as string}
						scale={scale}
					/>
				</Sequence>
			) : null}
			{/* The tile gate at the bottom only starts after the first stop. A clip intro opens on the map,
			    zooms out, turns the globe, and ungated those frames were captured before their tiles came
			    in -- a zoom-out rendered black round the previous frame's tiles. The classic intro was left
			    out on the grounds that it falls from the globe through tiles it already has, which holds
			    in one tab drawing every frame in order and not in a tab that starts at frame 3: rendered
			    in six tabs its globe came out black. */}
			<Sequence from={0} durationInFrames={INTRO_FRAMES + MARKER_DURATION}>
				<TileGate map={map} />
			</Sequence>
			<Sequence from={INTRO_FRAMES + MARKER_DURATION + (showFullscreenPhotos ? getPhotoDuration(styles.pictureSpeed) * reelData[0].photos?.length : 0)}>
				<Series>
					{reelData
						.map((destination: any, index) => {
							const path: any = buildLegPath(destination);
							const routeDistance = !!path?.geometry ? length(path) : 0;

							// Short legs used to get a flat 50 km altitude, which frames a country fine and a
							// city crawl not at all: five bars 1 to 6 km apart ended up as a cluster of dots
							// over the whole of Bangkok.
							//
							// Two regimes now. Under 25 km is a city: hold roughly zoom 17 to 18, which is
							// where Mapbox actually draws buildings as volumes -- above zoom 16 they are
							// there but too small to read, which is why the 3D came and went. Between 25 and
							// 100 km scale as before. The same numbers live in PathSequence (the arc and its
							// start) and MarkerSequence (the arrival), and all three have to agree.
							// Per-leg heights alone make the camera re-seek at every stop: on the Koh Phangan
							// loop the legs run 7.5 to 29.8 km, so the altitude wanted to swing by a factor
							// of three and each arrival ended with a visible pull-back. Blend each leg halfway
							// to the trip's own mean, which keeps long and short legs distinguishable while
							// holding the camera roughly still across a local trip.
							const plan = legPlans[index];
							const altitude = plan?.own || START_ALTITUDE;

							// The plan may have lengthened this leg beyond what its distance asks for, because
							// the climb it has to cover would otherwise happen too fast to read.
							const pathDuration = plan?.frames || 0;

							const hasNextDestination = index + 1 < reelData.length;

							return destination?.path?.length > 0 ? (
								<React.Fragment key={`${destination?.city}-${index}`}>
									{!!pathDuration && hasNextDestination ? (
										<Series.Sequence durationInFrames={pathDuration} key={`path-${destination?.city}-${index}`}>
											<PathSequence
												map={map}
												MAP_DURATION={isNaN(pathDuration) ? 0 : pathDuration}
												reelData={reelData}
												path={path}
												index={index}
												startAltitude={altitude}
												entryAltitude={plan?.entry}
												exitAltitude={plan?.exit}
												startPitch={START_PITCH}
												smooth={destination?.path?.length > 2}
												PATH_COLOR={transportColor}
												scale={scale}
											/>
										</Series.Sequence>
									) : null}
									{hasNextDestination && (
										<Series.Sequence durationInFrames={MARKER_DURATION} key={`marker-${destination?.city}-${index}`}>
											<MarkerSequence
												index={index + 1}
												map={map}
												MARKER_DURATION={MARKER_DURATION}
												reelData={reelData}
												currentTrip={currentTrip}
												startAltitude={plan?.exit}
												endAltitude={plan?.exit}
												startPitch={START_PITCH}
												showPhotoCards={showPhotoCards}
												cardStagger={cardStagger}
												cardStiffness={cardStiffness}
												scale={scale}
											/>
										</Series.Sequence>
									)}
									{showFullscreenPhotos && hasNextDestination && reelData?.[index + 1]?.photos?.length > 0 ? (
										<Series.Sequence
											durationInFrames={getPhotoDuration(styles.pictureSpeed) * reelData?.[index + 1]?.photos?.length}
											key={`photo-${destination?.city}-${index}`}
										>
											<PhotoSequence
												photos={reelData[index + 1].photos}
												city={reelData[index + 1]?.city}
												nights={reelData[index + 1]?.duration}
												PHOTO_DURATION={getPhotoDuration(styles.pictureSpeed)}
												journalTitle={reelData[index + 1]?.journalTitle}
												showJournalTitle={styles.showJournalTitle}
												scale={scale}
												objectFit={styles.pictureFit}
												isTemplate={dayBadges}
												dayIndex={reelData[index + 1]?.firstDayNumber ?? index + 2}
												lastDayIndex={reelData[index + 1]?.lastDayNumber}
												isLandscape={styles.orientation === "landscape"}
											/>
											{renderLogo()}
										</Series.Sequence>
									) : null}
								</React.Fragment>
							) : null;
						})
						.filter(Boolean)}

					{/* `showOutro` had a working checkbox in ReelEditor and was set in three places, but
					    nothing ever read it -- the outro rendered regardless. Honour it here, and in
					    calculateMetaData below, or the composition is longer than what it draws. */}
					<Series.Sequence durationInFrames={styles.showOutro === false ? 1 : OUTRO_DURATION}>
						{styles.showOutro === false ? (
							// Remotion refuses a childless Series.Sequence, so the "no outro" case is one
							// transparent frame rather than nothing at all.
							<AbsoluteFill />
						) : (
							// One composed card for both B2C and B2B: the branded case swaps the mark and
							// wordmark rather than falling back to a black fade, which is what it did
							// while the outro was a video file and a branded one would have meant a
							// fourth file to keep in step.
							<ReelOutro
								mark={styles.branding?.isCustomBranded ? styles.branding?.squareLogo : undefined}
								wordmark={styles.branding?.isCustomBranded ? styles.branding?.logo : undefined}
								brandName={styles.branding?.isCustomBranded ? styles.branding?.companyName : undefined}
								routeColor={styles.branding?.colors?.secondary}
								reelData={reelData}
								countries={currentTrip?.countries}
								tripName={currentTrip?.name}
								stats={[
									currentTrip?.numberOfDestinations ? `${currentTrip.numberOfDestinations} stops` : null,
									currentTrip?.countries?.length ? `${currentTrip.countries.length} ${currentTrip.countries.length === 1 ? "country" : "countries"}` : null,
									currentTrip?.duration ? `${currentTrip.duration} ${currentTrip.duration === 1 ? "day" : "days"}` : null,
								]
									.filter(Boolean)
									.join("  ·  ")}
							/>
						)}
					</Series.Sequence>
				</Series>
			<TileGate map={map} />
			</Sequence>
		</>
	);
};

/**
 * Holds each frame until Mapbox has actually drawn it.
 *
 * There was one `delayRender` in this file and it waited for the map's initial `load`. After that
 * every frame was captured whenever Remotion got to it, whether or not the tiles for wherever the
 * camera had just moved had arrived. Measured on the Bangkok-to-Koh-Phangan film, 14% of frames came
 * out as empty background -- worst over the island, where a cycling route at 5,500 m needs a lot of
 * satellite tiles for a place with no cache to speak of. It also made two renders of the same input
 * differ, which is why chasing it through the camera code never worked: the geometry was fine.
 *
 * Rendered after the sequences so its effect runs after theirs, i.e. after the camera has moved.
 * It asks the tile sources and the composition's own GeoJSON sources whether they are loaded, never
 * waiting for `idle` -- the instrumented render on 2026-09-20 showed 46% of frames waiting on `idle`,
 * which also waits for label placement the capture does not need. When it approves a frame while
 * rendering, it shows a copy of that draw over the map (`approvedFrame.ts`), so the screenshot gets
 * the frame it approved and not a later draw.
 */
/** How long a frame may wait for its tiles before it is shipped without them. */
const VALVE_MS = 20000;
/** The same, for a frame right after one the valve had to release. */
const VALVE_AFTER_VALVE_MS = 1000;
/**
 * Whether the last frame this tab drew was released by the valve rather than by its tiles.
 *
 * A view that has not got its tiles in 20 s is stuck on one Mapbox will not finish, and the next
 * frame, a thirtieth of a second further on, is stuck on the same one. Replaying a 53-stop reel on
 * the render box (2026-10-03), 13 frames in a row over Koh Rong waited out the full 20 s each: 4.6
 * of the render's 25 minutes, while every tile around the island answered the box in under half a
 * second. Those frames ship the same way whether the wait is 20 s or 1 s, so only the first of a
 * run gets the long one. A frame whose tiles do arrive puts the long wait back.
 */
let releasedByValve = false;

export const TileGate = ({ map }: { map: any }) => {
	const frame = useCurrentFrame();
	// The basemap, terrain and imagery sources -- see tileSourceIds for why the composition's own
	// GeoJSON sources are deliberately not on this list.
	const tileSources = useMemo(() => (map ? tileSourceIds(map) : []), [map]);
	// Mapbox Standard, whose sources that list cannot see. Asked once, like the list: it is a whole
	// serialisation of the style, too slow for every poll.
	const styleHasImports = useMemo(() => (map ? hasImports(map) : false), [map]);
	// The route lines, stop markers and transport dot. Asked once per map, like the lists above.
	const ownSources = useMemo(() => (map ? geojsonSourceIds(map) : []), [map]);

	useEffect(() => {
		if (!map) return undefined;
		const handle = delayRender(`Waiting for map tiles on frame ${frame}`, { timeoutInMilliseconds: 60000 });
		let done = false;
		const rendering = getRemotionEnvironment().isRendering;
		const ready = () => tilesLoaded(map, tileSources, styleHasImports) && sourcesLoaded(map, ownSources);
		// Asked on Mapbox's own `render` event, which fires after a draw. Each draw first works out
		// which tiles the current camera needs, and nothing else runs between that and the event --
		// so when the answer is yes there, the canvas already shows every tile this camera needs. A
		// tile that is still coming makes the map draw again when it lands, which asks again.
		//
		// This used to ask one animation frame after the camera moved, on the assumption that Mapbox
		// had drawn by then. In one tab drawing every frame in order it mostly had, and the tiles
		// barely changed from one frame to the next anyway. In six tabs on a busy box, each tab
		// jumping six frames at a time, it often had not: the caches still described the previous
		// camera, fully loaded, the gate said yes, and the frame came out with black squares where
		// the new view's satellite should have been.
		const onRender = () => {
			if (done || !ready()) return;
			// Still inside the draw that was just judged complete: the one moment its pixels can be copied.
			if (rendering) showApprovedFrame(map, true);
			finish();
		};
		const finish = (byValve = false) => {
			if (done) return;
			done = true;
			clearTimeout(valve);
			map.off("render", onRender);
			releasedByValve = byValve;
			continueRender(handle);
		};
		// The release valve: a frame that never gets its tiles is better shipped imperfect than failing
		// the whole render. It shows the live map, as every frame did before the copy.
		const valve = setTimeout(
			() => {
				if (rendering) showApprovedFrame(map, false);
				finish(true);
			},
			releasedByValve ? VALVE_AFTER_VALVE_MS : VALVE_MS
		);
		map.on("render", onRender);
		// A frame whose camera did not move (a photo, a stop) would otherwise wait for a draw that
		// nothing asks for.
		map.triggerRepaint();
		return () => finish(releasedByValve);
	}, [frame, map, tileSources, styleHasImports, ownSources]);

	return null;
};

/**
 * The film's length in frames, for a given `reelData` and `styles`. Pulled out of `calculateMetaData`
 * so ReelEditor's preview player can ask the same question instead of keeping its own copy -- a
 * second copy is exactly how ReelEditor's `calculateDuration` drifted from this one before (no
 * `planLegs` extension, and a different rule for the branded outro).
 */
export const calculateReelDuration = (reelData: any[], styles: any): { durationInFrames: number } => {
	// The film is what the composition lays out, so it ends where that layout ends. Adding the parts up a
	// second time drifted from the JSX: a stop in the same place as the one before (a leg of 0 frames) is
	// still drawn, the sum left its marker out, and every such reel lost a second off its end.
	const segments = reelSegments(reelData, styles);
	return { durationInFrames: Math.floor(segments[segments.length - 1].to) };
};

/** One stretch of the film: what is on screen from frame `from` (inclusive) to frame `to` (exclusive). */
export type ReelSegment = {
	kind: "intro" | "marker" | "photos" | "leg" | "outro";
	from: number;
	to: number;
	/** The stop for a marker or its photos, the leg for a leg (a leg is numbered by the stop it leaves), 0 for the intro and the outro. */
	index: number;
};

/**
 * The film's layout in absolute frames, from the composition's own rules: the intro, the first stop
 * and its photos, then for every destination that has a path its leg, the stop it arrives at and that
 * stop's photos, and the outro.
 *
 * It exists for a render that cuts the film into frame ranges and renders them in separate tabs. A tab
 * that starts partway in has to know what it skipped -- which legs are already drawn, where it is on
 * the route -- and the only place that is decided is the JSX of `CompositionComponent`, so this mirrors
 * that JSX, rule for rule, and asks the same helpers it does. Anything that changes what the JSX lays
 * out has to change this alongside it; `calculateMetaData` checks that the two still add up to the
 * film's length and does not publish the layout when they do not.
 */
export const reelSegments = (reelData: any[], styles: any): ReelSegment[] => {
	const segments: ReelSegment[] = [];
	let cursor = 0;
	const add = (kind: ReelSegment["kind"], frames: number, index = 0) => {
		segments.push({ kind, from: cursor, to: cursor + frames, index });
		cursor += frames;
	};
	const showsFullscreenPhotos = photoModeOf(styles) === "fullscreen";
	const hasPhotos = (stop: number) => showsFullscreenPhotos && reelData?.[stop]?.photos?.length > 0;
	const photoFrames = (stop: number) => getPhotoDuration(styles.pictureSpeed) * reelData?.[stop]?.photos?.length;

	add("intro", introFrames(styles));
	add("marker", MARKER_DURATION, 0);
	if (hasPhotos(0)) add("photos", photoFrames(0), 0);

	const legPlans = planReelLegs(reelData, styles);
	(reelData || []).forEach((destination: any, index: number) => {
		// A destination with no path leaves nothing in the Series: not its leg, nor the stop after it.
		if (!(destination?.path?.length > 0)) return;
		const pathDuration = legPlans[index]?.frames || 0;
		const hasNextDestination = index + 1 < reelData.length;
		if (!!pathDuration && hasNextDestination) add("leg", pathDuration, index);
		if (hasNextDestination) add("marker", MARKER_DURATION, index + 1);
		if (hasNextDestination && hasPhotos(index + 1)) add("photos", photoFrames(index + 1), index + 1);
	});

	add("outro", styles.showOutro === false ? 1 : OUTRO_DURATION);
	return segments;
};

/**
 * The frame each leg starts on, by leg index, for `planWarmupKeyframes`. A leg the film does not draw
 * starts where its stop's marker does, which is where it would have been; one whose destination has no
 * path at all is NaN, and a view with no frame is simply never left out.
 */
export const legStartFrames = (segments: ReelSegment[], stopCount: number): number[] =>
	Array.from({ length: Math.max(0, stopCount - 1) }, (_, leg) => {
		const start = segments.find((s) => s.kind === "leg" && s.index === leg) ?? segments.find((s) => s.kind === "marker" && s.index === leg + 1);
		return start ? start.from : NaN;
	});

/** Whether the layout is whole: it starts at 0, has no gap or overlap, lands on frames, and ends where the film does. */
const segmentsFillFilm = (segments: ReelSegment[], durationInFrames: number) =>
	segments.length > 0 &&
	segments.every((s, i) => Number.isInteger(s.from) && Number.isInteger(s.to) && s.from === (i === 0 ? 0 : segments[i - 1].to)) &&
	segments[segments.length - 1].to === durationInFrames;

const calculateMetaData = async ({ props }) => {
	const { styles } = props;
	// Marketing renders can ask for every unplanned leg on the road (roadRoutes.ts). It happens here,
	// before the length is worked out, because a leg's length is what sets its duration.
	const reelData = styles?.roadRoutes ? await withRoadRoutes(props.reelData) : props.reelData;

	const { durationInFrames } = calculateReelDuration(reelData, styles);

	const multiplier = styles.isHD ? 2 : 1;

	const width = (styles.orientation === "portrait" ? 540 : 960) * multiplier;
	const height = (styles.orientation === "portrait" ? 960 : 540) * multiplier;

	// The layout a chunked render cuts the film by. Published only when it is the film: a layout that
	// ends anywhere but at the film's last frame would send a tab to the wrong place, and the render
	// worker falls back to one tab when the prop is missing. Whatever an earlier resolution left in the
	// props goes too, so a stale layout cannot survive a film that no longer has one.
	const segments = reelSegments(reelData, styles);
	const resolvedProps: any = { ...props, reelData };
	delete resolvedProps.reelSegments;
	if (segmentsFillFilm(segments, durationInFrames)) {
		resolvedProps.reelSegments = segments;
	} else {
		console.warn(
			`[reel] the segment layout ends at frame ${segments[segments.length - 1]?.to} but the film is ${durationInFrames} frames: not publishing reelSegments, a chunked render has to use one tab`
		);
	}

	return {
		durationInFrames,
		props: resolvedProps,
		width,
		height,
	};
};

const TikTokVideo: React.FC = () => {
	return (
		<>
			<Composition
				//@ts-ignore
				component={CompositionComponent}
				fps={30}
				durationInFrames={1200}
				id="reel"
				width={1080}
				height={1920}
				defaultProps={{ currentTrip: {}, reelData: [], styles: getDefaultReelEditorStyles() }}
				calculateMetadata={calculateMetaData}
			/>
		</>
	);
};

export default TikTokVideo;
